import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar.tsx';
import { useAuth } from '../contexts/AuthContext.tsx';
import usePageTracking from '../hooks/usePageTracking.ts';
import { APP_CONFIG } from '../constants/appConfig.ts';
import { getLeaveRequests } from '../services/annualLeaveService.ts';

interface MainLayoutProps {
  children: React.ReactNode;
}

// 메인 레이아웃 컴포넌트 - 사이드바와 메인 콘텐츠 영역을 배치
const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [pendingLeaveCount, setPendingLeaveCount] = useState(0);
  const { user, logout, hasPermission } = useAuth();
  const navigate = useNavigate();
  const userMenuRef = useRef<HTMLDivElement>(null);

  // 페이지 방문 추적
  usePageTracking();

  // 승인 권한 확인
  const canApproveLeave = hasPermission('approve_annual_leave');


  // 승인 대기 중인 연차 요청 개수 조회
  useEffect(() => {
    const fetchPendingLeaveCount = async () => {
      if (!canApproveLeave) {
        return;
      }

      try {
        const pendingRequests = await getLeaveRequests({ status: ['pending'] });
        setPendingLeaveCount(pendingRequests.length);
      } catch (error) {
        console.error('[MainLayout] 승인 대기 연차 조회 실패:', error);
        setPendingLeaveCount(0);
      }
    };

    fetchPendingLeaveCount();
  }, [canApproveLeave, user]);
  
  // 외부 클릭 시 드롭다운 메뉴 닫기
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
    };

    if (userMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [userMenuOpen]);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const toggleUserMenu = () => {
    setUserMenuOpen(!userMenuOpen);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleUserManagement = () => {
    setUserMenuOpen(false);
    navigate('/user-management');
  };

  const handleProfile = () => {
    setUserMenuOpen(false);
    navigate('/profile');
  };

  // Role display mapping
  const roleDisplayNames: Record<string, string> = {
    admin: '관리자',
    manager: '매니저',
    viewer: '뷰어',
    employee: '직원'
  };

  return (
    <div className="flex h-screen bg-gray-100">
      {/* 모바일 사이드바 오버레이 */}
      <div 
        className={`fixed inset-0 z-20 bg-black bg-opacity-50 transition-opacity md:hidden ${
          sidebarOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setSidebarOpen(false)}
      />

      {/* 사이드바 모바일 */}
      <div 
        className={`fixed z-30 inset-y-0 left-0 w-44 transition duration-300 transform bg-blue-800 md:translate-x-0 md:relative md:flex-shrink-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <Sidebar />
      </div>

      {/* 메인 콘텐츠 */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* 헤더 */}
        <header className="bg-white shadow-sm z-10">
          <div className="px-4 py-2 flex justify-between items-center">
            <div className="flex items-center">
              <button
                className="text-gray-600 md:hidden focus:outline-none"
                onClick={toggleSidebar}
              >
                <svg
                  className="h-6 w-6"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              <div className="text-xl text-blue-800 font-semibold md:hidden ml-2">
                {APP_CONFIG.FULL_APP_NAME}
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600">
                {new Date().toLocaleDateString('ko-KR', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  weekday: 'long'
                })}
              </span>

              {/* 연차 승인 대기 배지 (관리자/매니저만 표시) */}
              {(() => {
                const shouldShowBadge = canApproveLeave && pendingLeaveCount > 0;
                console.log('[MainLayout Badge Render] 배지 렌더링 체크:', {
                  canApproveLeave,
                  pendingLeaveCount,
                  shouldShowBadge
                });
                return shouldShowBadge;
              })() && (
                <button
                  onClick={() => navigate('/annual-leave')}
                  className="flex items-center space-x-1 bg-yellow-500 text-white px-3 py-1.5 rounded-full hover:bg-yellow-600 transition-colors shadow-sm"
                  title="연차 승인 대기 항목 보기"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-xs font-medium">
                    승인 대기 {pendingLeaveCount}건
                  </span>
                </button>
              )}

              {user && (
                <div className="relative" ref={userMenuRef}>
                  {/* 사용자 정보 클릭 영역 */}
                  <button
                    onClick={toggleUserMenu}
                    className="flex items-center space-x-2 text-sm text-gray-700 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 rounded-lg px-3 py-2 transition-colors"
                  >
                    <div className="flex items-center space-x-2">
                      {/* 사용자 아바타 */}
                      <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-medium">
                        {(user.displayName || user.email).charAt(0).toUpperCase()}
                      </div>
                      
                      <div className="text-left">
                        <div className="font-medium">{user.displayName || user.email}</div>
                        <div className="text-gray-500 text-xs">
                          {roleDisplayNames[user.role] || user.role}
                        </div>
                      </div>
                      
                      {/* 드롭다운 화살표 */}
                      <svg 
                        className={`w-4 h-4 text-gray-400 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`}
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </button>

                  {/* 드롭다운 메뉴 */}
                  {userMenuOpen && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200">
                      <button
                        onClick={handleProfile}
                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                      >
                        <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        내 프로필
                      </button>
                      
                      {hasPermission('manage_users') && (
                        <button
                          onClick={handleUserManagement}
                          className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                        >
                          <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-2.25" />
                          </svg>
                          사용자 관리
                        </button>
                      )}
                      
                      <hr className="my-1" />
                      
                      <button
                        onClick={handleLogout}
                        className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                      >
                        <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                        로그아웃
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </header>

        {/* 메인 콘텐츠 영역 */}
        <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-100 p-4">
          {children}
        </main>
      </div>
    </div>
  );
};

export default MainLayout; 